// ============================================================================
// Canonical lifecycle transition table.
// ============================================================================
// SELF-CONTAINED (no imports) so it can be mirrored byte-for-byte into the Deno
// Edge Function. The mirror lives at:
//     supabase/functions/entity-action/transitions.ts
// The two copies MUST stay identical — enforced by src/lib/lifecycle/transitions.test.ts.
//
// Status is never a free dropdown. Each transition declares:
//   - requires:     field names that must be present (non-null) on the entity
//                   or in the action payload before the transition is allowed.
//   - sideEffects:  atomic operations the server runs as part of the transition.
// `from: '*'` means "from any state".
// ============================================================================

export type LifecycleEntity = 'lead' | 'quote' | 'project';

export interface Transition {
  from: string;
  to: string;
  requires?: string[];
  sideEffects?: string[];
}

export const TRANSITIONS: Record<LifecycleEntity, Transition[]> = {
  lead: [
    { from: 'new', to: 'contacted' },
    { from: 'new', to: 'quoted', requires: ['linked_quote_id'] },
    { from: 'contacted', to: 'qualified', requires: ['phone'] },
    { from: 'contacted', to: 'quoted', requires: ['linked_quote_id'] },
    { from: 'qualified', to: 'quoted', requires: ['linked_quote_id'] },
    { from: 'quoted', to: 'converted', sideEffects: ['create_customer', 'accept_quote'] },
    { from: '*', to: 'disqualified', requires: ['lost_reason'] },
  ],
  quote: [
    { from: 'draft', to: 'sent' },
    { from: 'sent', to: 'viewed' },
    { from: 'sent', to: 'accepted' },
    { from: 'sent', to: 'rejected' },
    { from: 'viewed', to: 'accepted' },
    { from: 'viewed', to: 'rejected' },
    { from: '*', to: 'expired' },
  ],
  project: [
    { from: 'quote', to: 'active' },
    { from: 'active', to: 'paused' },
    { from: 'paused', to: 'active' },
    { from: 'active', to: 'completed' },
    { from: 'completed', to: 'active' },
    { from: 'cancelled', to: 'active' },
    { from: '*', to: 'cancelled' },
  ],
};
